import React, { useState } from 'react';

import { CSVLink } from 'react-csv';

import useQuery from '../../../helpers/JIFI/useQuery';

import SupplyChainNav from '../../../components/supply-chain/nav.js';
import { productOf } from './sku-family';
import { Col } from '@shadcn/components/ui/col.jsx';
import { Card, CardContent as CardBody } from '@shadcn/components/ui/card.jsx';
import { Button } from '@shadcn/components/ui/button.jsx';
import { Input } from '@shadcn/components/ui/input.jsx';
import { TableSkeleton } from '@shadcn/components/ui/table.jsx';

const REGIONS = ['AU', 'US', 'UK', 'EU'];

const fmtMoney = (minor) => (minor != null ? (minor / 100).toFixed(2) : '-');

// The physical stock buckets a holding is valued over, as the API names their units.
const BUCKETS = [
  { key: 'onHandUnits', label: 'On Hand' },
  { key: 'committedUnits', label: 'Committed', title: 'Picked or sent to a 3PL, not yet shipped' },
  { key: 'displayUnits', label: 'Display' },
  { key: 'damagedUnits', label: 'Damaged' },
  { key: 'quarantineUnits', label: 'Quarantine' },
];

// A stale row was valued on hand only, before holding covered every bucket, so its
// on-hand units are the units its value covers.
const unitsOf = (r) => (r.stale ? r.onHandUnits ?? 0 : r.totalUnits ?? 0);
const basisLabel = (stale) => (stale ? 'On hand only (stale)' : 'Every bucket');

// COGS holding valuation, grouped by product: every physical bucket (on hand, committed,
// display, damaged, quarantine) x regional rate.
const CogsHoldingList = () => {
  const dateString = new Date().toDateString().replace(/ /g, '-');
  const [region, setRegion] = useState('AU');
  const [location, setLocation] = useState('');
  const [skuInput, setSkuInput] = useState('');
  const [sku, setSku] = useState('');

  const [rows, rowsState] = useQuery({
    url: `/cogs/holding`,
    queryData: { region, sku },
  });

  const data = Array.isArray(rows) ? rows : [];

  // Location options come from the full region dataset; the selector then scopes
  // the rows below (empty = all locations in the region).
  const locationOptions = Array.from(new Set(data.map((r) => r.locationCode).filter(Boolean))).sort();
  const scoped = location ? data.filter((r) => r.locationCode === location) : data;

  // Aggregate per SKU across the scoped locations (rate is per sku/region,
  // identical across that region's locations — so sum the units + holding value).
  const bySku = {};
  for (const r of scoped) {
    if (!bySku[r.sku]) {
      bySku[r.sku] = {
        sku: r.sku,
        region: r.region,
        rateMinorUnits: r.rateMinorUnits,
        ...Object.fromEntries(BUCKETS.map((b) => [b.key, 0])),
        totalUnits: 0,
        holdingValueMinorUnits: 0,
        stale: false,
      };
    }
    for (const b of BUCKETS) bySku[r.sku][b.key] += r[b.key] ?? 0;
    bySku[r.sku].totalUnits += unitsOf(r);
    bySku[r.sku].holdingValueMinorUnits += r.holdingValueMinorUnits ?? 0;
    bySku[r.sku].stale = bySku[r.sku].stale || !!r.stale;
  }
  const staleRows = scoped.filter((r) => r.stale).length;

  // Group SKUs by product.
  const groupsMap = {};
  for (const s of Object.values(bySku)) {
    const p = productOf(s.sku);
    (groupsMap[p] = groupsMap[p] || []).push(s);
  }
  const groups = Object.keys(groupsMap)
    .sort()
    .map((product) => {
      const items = groupsMap[product].sort((a, b) => a.sku.localeCompare(b.sku));
      const buckets = Object.fromEntries(
        BUCKETS.map((b) => [b.key, items.reduce((sum, i) => sum + (i[b.key] ?? 0), 0)]),
      );
      const units = items.reduce((sum, i) => sum + (i.totalUnits ?? 0), 0);
      const holding = items.reduce((sum, i) => sum + (i.holdingValueMinorUnits ?? 0), 0);
      const stale = items.some((i) => i.stale);
      // Average COGS = mean of the distinct SKU rates in the group.
      const rateVals = items.map((i) => i.rateMinorUnits).filter((v) => v != null);
      const avgRate = rateVals.length
        ? Math.round(rateVals.reduce((s, v) => s + v, 0) / rateVals.length)
        : null;
      return { product, items, buckets, units, holding, avgRate, stale };
    });

  const totalValue = groups.reduce((s, g) => s + g.holding, 0);
  const totalUnits = groups.reduce((s, g) => s + g.units, 0);

  const csvData = [
    ['Product', 'SKU', 'Region', ...BUCKETS.map((b) => b.label), 'Total Units', 'COGS Rate', 'Holding Value', 'Value Basis'],
    ...groups.flatMap((g) => [
      [
        g.product,
        '',
        region,
        ...BUCKETS.map((b) => g.buckets[b.key]),
        g.units,
        fmtMoney(g.avgRate),
        fmtMoney(g.holding),
        basisLabel(g.stale),
      ],
      ...g.items.map((i) => [
        '',
        i.sku,
        i.region,
        ...BUCKETS.map((b) => i[b.key]),
        i.totalUnits,
        fmtMoney(i.rateMinorUnits),
        fmtMoney(i.holdingValueMinorUnits),
        basisLabel(i.stale),
      ]),
    ]),
  ];

  return (
    <SupplyChainNav>
      <div>
        <Col className="w-full">
          <Card className="pt-6 mb-10">
            <CardBody>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h4 className="mb-3">COGS Holding Valuation</h4>
                <CSVLink
                  style={{ float: 'right', marginBottom: '1.25rem' }}
                  data={csvData}
                  filename={`COGS-HOLDING-${region}-${dateString}.csv`}
                >
                  <Button type="button" variant="secondary" className="btn-md btn-rounded">
                    CSV
                  </Button>
                </CSVLink>
              </div>

              <div className="flex gap-4 mb-3">
                <div>
                  <label className="control-label">MARKET</label>
                  <select
                    className="form-control"
                    value={region}
                    onChange={(e) => {
                      setRegion(e.target.value);
                      setLocation('');
                    }}
                  >
                    {REGIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="control-label">LOCATION</label>
                  <select
                    className="form-control"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  >
                    <option value="">All locations</option>
                    {locationOptions.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="control-label">FILTER SKU</label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      className="form-control"
                      placeholder="Filter by SKU…"
                      value={skuInput}
                      onChange={(e) => setSkuInput(e.target.value)}
                      onKeyDown={(e) => (e.key === 'Enter' ? setSku(skuInput.trim()) : null)}
                    />
                    <Button type="button" variant="primary" onClick={() => setSku(skuInput.trim())}>
                      Apply
                    </Button>
                  </div>
                </div>
              </div>

              <p className="text-muted-foreground">
                {region} · {location || 'All locations'} · {totalUnits} units (every bucket) · total
                holding value: <strong>{fmtMoney(totalValue)}</strong>
              </p>
              {staleRows > 0 ? (
                <p className="text-muted-foreground">
                  {staleRows} holding row(s) were valued on hand only, before holding covered every
                  bucket, so the totals mix two bases until holdings are revalued.
                </p>
              ) : null}

              <div className="table-responsive">
                <table className="table table-centered table-nowrap">
                  <thead>
                    <tr>
                      <th>Product / SKU</th>
                      {BUCKETS.map((b) => (
                        <th key={b.key} className="text-center" title={b.title}>
                          {b.label}
                        </th>
                      ))}
                      <th className="text-center">Total Units</th>
                      <th className="text-center">COGS Rate</th>
                      <th className="text-center">Holding Value</th>
                      <th className="text-center">Value Basis</th>
                    </tr>
                  </thead>
                  {rowsState === 'success' ? (
                    <tbody>
                      {groups.length > 0 ? (
                        groups.map((g) => (
                          <React.Fragment key={g.product}>
                            <tr style={{ background: '#f6f6f6' }}>
                              <td className="font-weight-bold">{g.product}</td>
                              {BUCKETS.map((b) => (
                                <td key={b.key} className="text-center font-weight-bold">
                                  {g.buckets[b.key]}
                                </td>
                              ))}
                              <td className="text-center font-weight-bold">{g.units}</td>
                              <td className="text-center font-weight-bold">
                                {fmtMoney(g.avgRate)}
                              </td>
                              <td className="text-center font-weight-bold">
                                {fmtMoney(g.holding)}
                              </td>
                              <td className="text-center font-weight-bold">{basisLabel(g.stale)}</td>
                            </tr>
                            {g.items.map((i) => (
                              <tr key={`${g.product}-${i.sku}`}>
                                <td style={{ paddingLeft: '2rem' }}>{i.sku}</td>
                                {BUCKETS.map((b) => (
                                  <td key={b.key} className="text-center">
                                    {i[b.key] ?? 0}
                                  </td>
                                ))}
                                <td className="text-center">{i.totalUnits ?? 0}</td>
                                <td className="text-center">{fmtMoney(i.rateMinorUnits)}</td>
                                <td className="text-center">
                                  {fmtMoney(i.holdingValueMinorUnits)}
                                </td>
                                <td className="text-center">{basisLabel(i.stale)}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={BUCKETS.length + 5} className="text-center">
                            No holding rows
                          </td>
                        </tr>
                      )}
                    </tbody>
                  ) : (
                    <TableSkeleton
                      columns={BUCKETS.length + 5}
                      perPage={10}
                      centerIndex={Array.from({ length: BUCKETS.length + 4 }, (_, n) => n + 1)}
                    />
                  )}
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </div>
    </SupplyChainNav>
  );
};

export default CogsHoldingList;
